import React, { useState, useEffect, useCallback } from 'react';
import { Users, Briefcase } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const CaseVolumeWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getCaseVolumeByAttorney();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching case volume:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
            <div className="h-10 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const maxCases = data ? Math.max(...data.attorneys.map(a => a.total_open), 1) : 1;

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="h-5 w-5 text-primary-600 mr-2" />
            Case Volume by Attorney
          </h3>
          {data && (
            <span className="text-sm text-gray-500 font-medium">
              {data.firm_total} open cases
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        {!data || data.attorneys.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-gray-400 mb-3">
              <Briefcase className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600 font-medium">No open cases</p>
            <p className="text-sm text-gray-500 mt-1">No active cases assigned to attorneys</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.attorneys.map((attorney) => {
              const widthPct = Math.max((attorney.total_open / maxCases) * 100, 4);
              return (
                <div key={attorney.attorney_id}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-900 truncate max-w-[60%]">
                      {attorney.attorney_name}
                    </span>
                    <div className="flex items-center space-x-3 text-xs">
                      {attorney.active_cases > 0 && (
                        <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                          {attorney.active_cases} active
                        </span>
                      )}
                      {attorney.intake_cases > 0 && (
                        <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                          {attorney.intake_cases} intake
                        </span>
                      )}
                      {attorney.pending_cases > 0 && (
                        <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
                          {attorney.pending_cases} pending
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 transition-all duration-500"
                      style={{ width: `${widthPct}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseVolumeWidget;
